import { AppContext, AdaptivePracticeContext } from "../../context/context";
import { decryptData, getTimeSpent } from '../../utils/utils';
import { Component, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ContentFactoryImpl, Content } from '@mathbuddy/adaptive-practice-engine';
import { PlayFrame } from "./frame/PlayFrame";
import { ls } from "../../localStorage";
import apis from "../../apis/api";
import { applyTheme, resolveTheme, Theme } from './theme';
import { Loader } from './Loader';


// SCORED single-question practice, for a student replaying ONE question of an ALREADY-MASTERED skill
// (launched from the shared Learning-Gap report's "Play" action). Unlike questionPreview (view only),
// each answer IS persisted through the normal adaptive-practice save endpoint, so play-service stamps
// mode='Practice' (the skill is Mastered -> the server derives Practice from the stored status) and the
// row feeds the report's live "after mastery" accuracy. No hint/2-attempt: each submit is one scored
// try (answers = [{isCorrect, answer}]). Repeatable — after answering, the same question reloads.


export function PracticeQuestion() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const QUERY = queryParams.get("query");
  const skillId = queryParams.get("skillId") || '';
  const questionId = queryParams.get("questionId") || '';
  const lang = queryParams.get("lang") || 'en';
  const platform = queryParams.get("platform") || 'web';
  const state: any = useContext(AppContext);

  const [currentUser, setCurrentUser] = useState<any>(null);
  // Theme chosen in student/teacher web settings, carried in the encrypted payload.
  const [theme, setTheme] = useState<Theme | undefined>(undefined);
  const [coins, setCoins] = useState<number | undefined>(undefined);
  const [practiceStart, setPracticeStart] = useState<{ tries: number, correct: number }>({ tries: 0, correct: 0 });

  useEffect(() => {
    try {
      if (!QUERY) throw new Error('Missing query');
      const query: { currentUser: any, coins?: number, practice?: { tries: number, correct: number }, theme?: Theme } = decryptData(QUERY);
      setTheme(query.theme);
      const user = query.currentUser;
      if (!user?.token || !user?.dbToken) throw new Error('Invalid session');
      ls.setToken(user.token);
      ls.setDBToken(user.dbToken);
      setCurrentUser(user);
      // total coins from the host's dashboard summary — display only, no extra API call
      if (typeof query.coins === 'number') setCoins(query.coins);
      // this question's stored practice history — the meter continues from it, like the coins
      if (query.practice && typeof query.practice.tries === 'number')
        setPracticeStart({ tries: query.practice.tries, correct: query.practice.correct || 0 });
    } catch (error) {
      console.log(error);
      state.messageForParent('close');
    }
  }, []);

  if (!currentUser || skillId === '' || questionId === '') return <Loader theme={theme} />;

  return (
    <PracticeQuestionPlayer
      currentUser={currentUser}
      skillId={skillId}
      questionId={questionId}
      lang={lang}
      platform={platform}
      coins={coins}
      practiceStart={practiceStart}
      theme={theme}
    />
  );
}

class PracticeQuestionPlayer extends Component<any, any> {
  static contextType = AppContext;
  context!: { windowHeight: string, windowWidth: string, messageForParent: any, navigateTo: any };

  constructor(props: any) {
    super(props);
    this.state = {
      // Optional `theme` prop from the parent; falls back to this screen's own
      // default. Resolved once - the theme cannot change between questions.
      theme: resolveTheme(props.theme),
      contentFactoryObj: new ContentFactoryImpl(),
      contentObj: new Content(),
      langObj: { number_lang: 'en', lang: '' },
      targetQuestionId: props.questionId,
      isLoader: true,
      hasError: false,
      response: { component: [] },
      startTime: new Date(),
      isNewSession: true,
      // seeded with the question's stored practice history (from the host) so the meter shows
      // the REAL running totals, not a fresh count per session
      practiced: props.practiceStart?.tries || 0,
      correct: props.practiceStart?.correct || 0,
      // passed by the host in the encrypted query (its redux dashboard summary) — no extra API
      // call; undefined hides the counter. Practice answers persist through the normal save path,
      // which awards coins for correct answers (skill status `coins += totalCorrectAnswers`), so
      // the +1s shown here are real, earned coins.
      coins: typeof props.coins === 'number' ? props.coins : undefined,
      isCoinAnimation: false,
    };
  }

  reloadTimer: any = null;

  componentDidMount = () => {
    // Paint the frame ONCE per screen - 21 custom properties, not a per-question cost.
    applyTheme(this.state.theme);
    this.loadQuestion();
  }

  componentWillUnmount = () => { if (this.reloadTimer) clearTimeout(this.reloadTimer); }

  loadQuestion = async () => {
    try {
      this.setState({ isLoader: true, hasError: false });
      const { skillId, questionId, lang, currentUser } = this.props;
      const countryCode = (currentUser.classroom_country_code || currentUser.country_code || 'IN').toLowerCase();
      const langObj = { number_lang: 'en', lang: `${lang}_${countryCode}` };

      await this.state.contentObj.loadSkillBySkillId(skillId, this.state.theme, langObj.lang);

      const totalQuestions = await this.state.contentObj.getNumberOfQuestions();
      let targetQuestionId = questionId;
      for (let pos = 0; pos < totalQuestions; pos++) {
        const q: { questionId: string } = await this.state.contentObj.loadQuestionByNumber(pos);
        if (q?.questionId?.toString() === questionId.toString()) { targetQuestionId = q.questionId; break; }
      }

      const response = await this.state.contentFactoryObj.displayQuestion(
        this.state.contentObj,
        this.state.contentObj.createContentTemplateObject(targetQuestionId, 'REACT_WEB'),
        langObj,
      );

      this.setState({ langObj, response, targetQuestionId, isLoader: false, startTime: new Date() });
    } catch (error) {
      console.log(error);
      this.setState({ isLoader: false, hasError: true });
    }
  }

  // One scored try. Persist through the normal endpoint; the server stamps mode='Practice' from the
  // stored (Mastered) skill status. Then reload the same question so the student can practise again.
  saveQuestionResponse = async (answerObj: { givenAnswer: any, actualAnswer: any, isCorrectAns: boolean }) => {
    try {
      const time_spent = getTimeSpent(this.state.startTime);
      const data = {
        questions: [{
          isNewSession: this.state.isNewSession,
          isReAttempt: false,
          user_id: this.props.currentUser.id,
          lo_id: this.props.skillId,
          question_id: this.state.targetQuestionId,
          review_obj: this.state.response.reviewObj,
          number_lang: this.state.langObj.number_lang,
          lang: this.state.langObj.lang,
          answers: [{ isCorrect: answerObj.isCorrectAns, answer: answerObj.givenAnswer }],
          isCorrect: answerObj.isCorrectAns,
          isSafeZone: false,
          isChallenge: false,
          isChallengeCompleted: false,
          blooms: this.state.response.blooms,
          learningGaps: this.state.response.learningGaps || [],
          time_spent,
          current_level: 'Mastered',   // post-mastery practice; server derives mode from STORED status
          classroom_id: this.props.currentUser.classroom_id,
        }],
      };
      await apis._saveQuestionResponse(data);
    } catch (error) {
      console.log('practiceQuestion save failed', error);
    } finally {
      this.setState((s: any) => ({
        isNewSession: false,
        practiced: s.practiced + 1,
        correct: s.correct + (answerObj.isCorrectAns ? 1 : 0),
        // +1 per correct answer, mirroring regular play (the save above banked it server-side)
        coins: answerObj.isCorrectAns && s.coins !== undefined ? s.coins + 1 : s.coins,
        isCoinAnimation: answerObj.isCorrectAns === true,
      }), () => {
        if (answerObj.isCorrectAns) {
          // let the coin animation land, then reload the SAME question automatically so the
          // child keeps practising without hunting for the reload button; a wrong answer stays
          // on screen so they can look at it and retry deliberately.
          this.reloadTimer = setTimeout(() => {
            this.setState({ isCoinAnimation: false });
            this.loadQuestion();
          }, 1700);
        }
      });
      // Tell the host shell about every practice try, so its report updates the question's live
      // accuracy locally (one submit = one try, matching the gap row's practice_tries $inc) —
      // no API refetch needed on the parent side.
      this.context.messageForParent('practiceAnswer', {
        question_id: this.state.targetQuestionId,
        lo_id: this.props.skillId,
        isCorrect: answerObj.isCorrectAns === true,
      });
    }
  }

  onClose = () => { this.context.messageForParent('close'); }

  render() {
    if (this.state.hasError) {
      return (
        <div className="frame-container d-flex flex-column justify-content-center align-items-center" style={{ gap: '16px', color: '#fff' }}>
          <span>Couldn't load this question.</span>
          <button className="skip-btn" onClick={this.onClose}>Close</button>
        </div>
      );
    }

    const acc = this.state.practiced ? Math.round((this.state.correct * 100) / this.state.practiced) : 0;

    return (
      <>
        <AdaptivePracticeContext.Provider value={{
          notificationMessage: '',
          coins: this.state.coins,
          streaks: undefined,
          isSafeZone: false,
          // the mastery-meter slot (right column, unused in practice) shows the live practice
          // stats instead: bar fill = accuracy %, label = "tried · accuracy". Hidden ('' level)
          // until the first answer, exactly like the meter itself.
          meter: {
            percentage: acc,
            level: this.state.practiced > 0 ? `${this.state.practiced} tried · ${acc}%` : '',
          },
          isLoader: this.state.isLoader,
          isCoinAnimation: this.state.isCoinAnimation,
          isShowStartPopup: false,
          isShowAdaptivePopup: false,
          isCloseFromClick: false,
          engineObj: this.state.response,
          options: {
            isToggleFullSceren: this.props.platform !== 'app',
            isShowReload: true,
            // unlike the teacher's silent /previewQuestion, practice is real play — allow the
            // coin/feedback sounds (the frame's own mute toggle still works)
            isMusicAllowed: true,
          },
          // reload the same question -> repeatable practice loop
          loadContent: this.loadQuestion,
          saveQuestionResponse: this.saveQuestionResponse,
          getPopUpInfo: () => ({}),
          setShowAdaptivePopup: this.onClose,
          onclose: this.onClose,
        } as any}>
          <PlayFrame></PlayFrame>
        </AdaptivePracticeContext.Provider>
      </>
    );
  }
}

export default PracticeQuestion;
